import { useCallback, useEffect, useRef, useState } from "react";
import {
  EMAIL_PREVIEW_MIN_WIDTH,
  emailPreviewMinWidthFor,
  measureEmailFrameHeight,
  measureEmailFrameWidth,
} from "./email-preview-frame";

/**
 * Shared sizing of the "as on desktop" email preview iframes (send preview,
 * sent-mail viewer). Plan eager-prancing-music W0 §5.
 *
 * - WIDTH is decided once per email: reset to EMAIL_PREVIEW_MIN_WIDTH, write
 *   the html, measure after layout (two frames), clamp. It is never re-measured
 *   from a resize event: `scrollWidth` is at least the viewport, so measuring
 *   on every resize lets viewport-relative content widen the frame forever.
 *   The measured width is written to the iframe directly AND to state, so a
 *   React commit can never leave the DOM at a stale width.
 * - HEIGHT and SCALE follow the content and the container continuously
 *   (late reflow, webfont swap, image load, dialog resize), as before.
 *
 * `write` puts the html into the iframe document (each caller has its own
 * transform). `html === null` means "nothing to show yet" and leaves the
 * frame untouched.
 */
export function useEmailPreviewFrame(
  html: string | null,
  write: (doc: Document, html: string) => void,
) {
  const containerRef = useRef<HTMLDivElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const widthRef = useRef(EMAIL_PREVIEW_MIN_WIDTH);
  const writeRef = useRef(write);
  writeRef.current = write;
  const [width, setWidth] = useState(EMAIL_PREVIEW_MIN_WIDTH);
  const [height, setHeight] = useState(0);
  const [scale, setScale] = useState(1);

  const applyWidth = useCallback((w: number) => {
    widthRef.current = w;
    if (iframeRef.current) iframeRef.current.style.width = `${w}px`;
    setWidth(w);
  }, []);

  const recalculate = useCallback(() => {
    const container = containerRef.current;
    const iframe = iframeRef.current;
    if (!container || !iframe) return;
    // Rects, never the root's scroll height: in quirks mode that is the
    // frame's own height, so the frame could grow but never shrink (W1-3).
    setHeight(measureEmailFrameHeight(iframe.contentDocument));
    setScale(Math.min(container.clientWidth / widthRef.current, 1));
  }, []);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;
    const handleLoad = () => recalculate();
    iframe.addEventListener("load", handleLoad);
    const resizeObserver = new ResizeObserver(() => recalculate());
    if (containerRef.current) resizeObserver.observe(containerRef.current);
    return () => {
      iframe.removeEventListener("load", handleLoad);
      resizeObserver.disconnect();
    };
    // `html` too: a caller may render the iframe only once content exists
    // (PEmailSent shows a spinner first) — re-attach when it appears.
  }, [recalculate, html]);

  useEffect(() => {
    if (html === null) return;
    const iframe = iframeRef.current;
    const doc = iframe?.contentDocument;
    if (!iframe || !doc) return;

    // Start above the mail's OWN breakpoint (640 for a 600 px mail, width + 40
    // for a wider one): below it the mail stacks and squeezes itself to the
    // frame, so no later measurement could widen it again.
    const minWidth = emailPreviewMinWidthFor(html);
    applyWidth(minWidth);
    writeRef.current(doc, html);

    // The iframe is sized to its content's height (it can't scroll), so a
    // height measured before layout settles CLIPS the tail of a long email.
    // `document.close()` in the writer fires `load` in browsers (a harmless
    // re-measure); the observer below still covers the late reflow — webfont
    // swap, image load — so the HEIGHT follows the content either way.
    let cancelled = false;
    const raf1 = requestAnimationFrame(() => {
      if (!cancelled) recalculate();
    });
    let raf3 = 0;
    const raf2 = requestAnimationFrame(() => {
      raf3 = requestAnimationFrame(() => {
        if (cancelled) return;
        applyWidth(measureEmailFrameWidth(iframe.contentDocument, minWidth));
        recalculate();
      });
    });
    const innerObserver = new ResizeObserver(() => recalculate());
    if (doc.documentElement) innerObserver.observe(doc.documentElement);
    if (doc.body) innerObserver.observe(doc.body);
    return () => {
      cancelled = true;
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      cancelAnimationFrame(raf3);
      innerObserver.disconnect();
    };
  }, [html, applyWidth, recalculate]);

  return { containerRef, iframeRef, width, height, scale };
}
